/**
 * Chip model for the Access panel's per-entitlement source badges.
 *
 * ## The bug this exists to remove
 *
 * `EntitlementProvenanceList` rendered one badge per entry in the entitlement's
 * `subscriptions[]` with no test on `isActive`, so a dead grant appeared as a
 * source of a live entitlement. On a real dev account, `HeapsterLive Access`
 * showed "● Active · Non-expiring" beside a purple `Stripe` chip while that
 * Stripe grant was `isActive: false`, expired months earlier, and only a manual
 * grant kept the entitlement alive. An admin answering "who is paying for this
 * user?" reads that chip as the answer.
 *
 * Nothing is removed to fix it: a dead source is still on the collapsed line,
 * as a muted chip that says so.
 *
 * Grouping by provider also resolves a second complaint: two live Stripe
 * subscriptions rendered as two byte-identical `Stripe` chips, which reads as a
 * duplicate-render bug rather than as a cardinality. `Stripe ×2` states it.
 *
 * ## Why no tooltip here says "paying"
 *
 * `trial_period_days: 90` is set on EVERY checkout session
 * (`api/routes/subscriptions.ts`), and `trialing` counts as active
 * (`lib/stripe/subscriptionStatus.ts`). So every subscriber is `trialing` for
 * their first three months, and "paying now" would be false for all of them —
 * a sentence a support admin would repeat to a customer who has been charged
 * nothing. Everything here says "active", never "paid".
 */

import type { SubscriptionEntitlementData } from '../types/entitlement';

export interface ProvenanceChip {
  /** Provider key ('stripe', 'supapass', 'redeem', …) or 'manual'. */
  kind: string;
  /** How many grants of this kind and state the entitlement holds. */
  count: number;
  /** False for a recorded source that is no longer granting. */
  active: boolean;
}

/**
 * One chip per (provider, active) pair, active first, then ended, manual last.
 *
 * The count is carried on BOTH sides. An earlier draft put it only on the
 * active chip, which quietly lost the difference between one and two ended
 * subscriptions from the collapsed line while keeping it for live ones.
 */
export function resolveProvenanceChips(
  subscriptions: SubscriptionEntitlementData[] | undefined,
  manual: { present: boolean; active: boolean }
): ProvenanceChip[] {
  const counts = new Map<string, ProvenanceChip>();

  for (const grant of subscriptions ?? []) {
    const key = `${grant.provider}:${grant.isActive ? 'active' : 'ended'}`;
    const existing = counts.get(key);
    if (existing) {
      existing.count += 1;
      continue;
    }
    counts.set(key, {
      kind: grant.provider,
      count: 1,
      active: grant.isActive,
    });
  }

  const chips = [...counts.values()].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return a.kind.localeCompare(b.kind);
  });

  // Manual last, matching the order the expanded detail renders its panels in.
  //
  // Its state comes from `sources`, NOT from the presence of the block. The
  // evaluator emits `manual` whenever a manual record exists — deliberately,
  // so admin-conferred access is never hidden — but pushes 'manual' into
  // `sources` only when that grant is live. Reading presence as liveness would
  // put an expired hand-grant in full colour on the collapsed row: the same
  // defect this module removes for subscriptions, on the other source type,
  // and made worse by fixing only one of them, since greying the dead ones
  // teaches the reader that colour means paying.
  if (manual.present) {
    chips.push({ kind: 'manual', count: 1, active: manual.active });
  }
  return chips;
}

/** Display label for a provenance kind: 'manual' → Manual, else Title case. */
export function provenanceLabel(kind: string): string {
  if (kind === 'manual') return 'Manual';
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

/** The chip's visible text, e.g. `Stripe`, `Stripe ×2`, `Stripe · ended`. */
export function chipText(chip: ProvenanceChip): string {
  const base = provenanceLabel(chip.kind);
  const counted = chip.count > 1 ? `${base} ×${chip.count}` : base;
  return chip.active ? counted : `${counted} · ended`;
}

/**
 * What the chip means, in a sentence a first-day admin can act on. Colour
 * cannot carry this: `stripe` is purple and `manual` sky, but every OTHER
 * provider shares one emerald — so a `redeem` grant (a redeemed code) and a
 * `supapass` subscription are the same colour with different words.
 */
export function chipTitle(chip: ProvenanceChip): string {
  if (chip.kind === 'manual') {
    return chip.active
      ? 'Given manually by an admin.'
      : 'Given manually by an admin, but it has expired.';
  }
  if (chip.kind === 'redeem') {
    return chip.active
      ? "From a redeemed code. There's no subscription behind it, so nothing shows under Billing."
      : 'From a redeemed code that has expired.';
  }
  const name = provenanceLabel(chip.kind);
  return chip.active
    ? `From a ${name} subscription that's still active. See the matching row under Billing below for dates and ids.`
    : `From a ${name} subscription that has ended.`;
}
